import { sliceHTML } from '@/lib/slice-html';
class ExpandableText extends HTMLElement {
  private content: HTMLElement | null = null;
  private toggleBtn: HTMLButtonElement | null = null;
  private moreLabel: string = 'Read more';
  private lessLabel: string = 'Read less';
  private maxLines: number = 4;
  private maxChars: number = 1000;
  private originalHtml: string = '';
  private truncatedHtml: string | null = null;

  connectedCallback() {
    this.content = this.querySelector('[data-expandable-content]');
    this.toggleBtn = this.querySelector('[data-expandable-toggle]');

    this.moreLabel = this.getAttribute('data-more-label') || this.moreLabel;
    this.lessLabel = this.getAttribute('data-less-label') || this.lessLabel;
    const maxLinesAttr = this.getAttribute('data-max-lines');
    this.maxLines = maxLinesAttr ? parseInt(maxLinesAttr, 10) || this.maxLines : this.maxLines;
    const maxCharsAttr = this.getAttribute('data-max-chars');
    this.maxChars = maxCharsAttr ? parseInt(maxCharsAttr, 10) || this.maxChars : this.maxChars;

    // Prepare truncated HTML that preserves tags
    if (this.content) {
      this.originalHtml = this.content.innerHTML;
      const fragment = sliceHTML(this.content, 0, this.maxChars);
      const div = document.createElement('div');
      div.appendChild(fragment);
      const trimmed = div.innerHTML.trim();
      this.truncatedHtml = trimmed.length < this.originalHtml.length ? this.addEllipsis(trimmed) : null;

      // If we have truncated HTML and container is collapsed, render it
      const isCollapsed = this.getAttribute('data-collapsed') !== 'false';
      if (this.truncatedHtml && isCollapsed) {
        this.content.innerHTML = this.truncatedHtml;
      }
    }

    // Only bind toggle if button exists (i.e., content exceeded threshold)
    if (this.toggleBtn) {
      this.toggleBtn.addEventListener('click', () => this.toggle());
      // Initial label/state based on host attribute for consistency
      const collapsed = this.getAttribute('data-collapsed') !== 'false';
      this.updateUi(collapsed);
    }
  }

  private toggle() {
    if (!this.toggleBtn) return;
    const collapsed = this.getAttribute('data-collapsed') !== 'false';
    this.updateUi(!collapsed, true);
  }

  private updateUi(collapsed: boolean, scroll: boolean = false) {
    if (!this.toggleBtn) return;
    this.setAttribute('data-collapsed', collapsed ? 'true' : 'false');
    this.toggleBtn.setAttribute('aria-expanded', (!collapsed).toString());
    // Prefer updating inner span label if present to avoid clobbering markup
    const labelSpan = this.toggleBtn.querySelector('span');
    const nextLabel = collapsed ? this.moreLabel : this.lessLabel;
    if (labelSpan) {
      labelSpan.textContent = nextLabel;
    } else {
      this.toggleBtn.textContent = nextLabel;
    }

    if (this.content) {
      if (collapsed) {
        if (this.truncatedHtml) this.content.innerHTML = this.truncatedHtml;
        const diff = this.originalHtml.length - (this.truncatedHtml?.length ?? 0);

        // If the difference is greater than 2 times the max chars, scroll to the top (respect reduced motion)
        if (scroll && diff > this.maxChars * 2) {
          const prefersReduced = this.prefersReducedMotion();
          this.content.scrollIntoView({
            behavior: prefersReduced ? 'auto' : 'smooth',
            block: 'start',
            inline: 'nearest',
          });
        }
      } else {
        this.content.innerHTML = this.originalHtml;
      }
    }
  }

  private prefersReducedMotion(): boolean {
    return (
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  private addEllipsis(input: string) {
    if (!input) return input;
    const isHtml = /<\/?[a-z][\s\S]*>/i.test(input);
    if (!isHtml) return input + '...';

    const wrapper = document.createElement('div');
    wrapper.innerHTML = input;

    // find the last non-empty text node
    const findLastTextNode = (node: Node): Node | null => {
      for (let i = node.childNodes.length - 1; i >= 0; i--) {
        const c = node.childNodes[i];
        if (c.nodeType === Node.TEXT_NODE && c.textContent && /\S/.test(c.textContent)) return c;
        if (c.nodeType === Node.ELEMENT_NODE) {
          const r: Node | null = findLastTextNode(c);
          if (r) return r;
        }
      }
      return null;
    };

    const t = findLastTextNode(wrapper);
    if (t) {
      const currentText = t.textContent ?? '';
      t.textContent = currentText.replace(/\s+$/, '') + '...';
      return wrapper.innerHTML;
    }

    // fallback: insert before last closing tag, allow trailing spaces
    return input.replace(/\s*<\/([a-z][a-z0-9]*)>\s*$/i, '...</$1>');
  }
}

if (!customElements.get('expandable-text')) {
  customElements.define('expandable-text', ExpandableText);
}
